/**
 * The CLIENT half of the WebSocket credential wire shape, reachable from browser code.
 *
 * This leaf depends on no runtime global, so it typechecks under the Workers lib and a DOM lib alike. That is
 * load-bearing, not incidental: `components/` consumers cannot reach `ws-identity-channel.ts`, whose
 * `echoBearerSubprotocol` derives Cloudflare's `webSocket`-on-`Response` and is TS2740 in a DOM-lib project. Keep this
 * leaf global-free, or browser callers go back to hand-inlining the offer array against `WS_BEARER_PROTOCOL` and the
 * one shape the server parses acquires copies that can disagree.
 */

import { WS_BEARER_PROTOCOL } from '@federa/const/websocket';

/**
 * The subprotocol list a browser offers: the `bearer` MARKER plus the token, matching the repo-wide shape
 * `extractWebSocketAuthToken` and `extractSubprotocolBearer` parse. With no token only the marker is offered, so the
 * server still has exactly one selectable value -- a client that offered subprotocols and gets none selected is
 * HARD-FAILED by the browser.
 *
 * The token-less arm is for the PUBLIC surfaces only (`/property/*`, where a guest upgrade is legitimate). On an
 * identity-bearing route the gate is fail-closed with no grace mode, so offering the bare marker there buys a 401 -- a
 * caller with no credential for such a route must not connect at all rather than hand this a null.
 */
export function buildClientSubprotocols(token: string | null | undefined): string[] {
	return token ? [WS_BEARER_PROTOCOL, token] : [WS_BEARER_PROTOCOL];
}
